import React, { useState } from 'react'
import '../../markdown/Markdown/styles.scss'
import { useIntl } from 'react-intl'
import AlertDialog from '../../../dialogs/AlertDialog'
import TemplateDialog from '../../../dialogs/TemplateDialog'
import WebIcon from '@mui/icons-material/WebRounded'
import IconButton from '@mui/material/IconButton'
import Tooltip from '../../../tooltips/Tooltip'
import { Box, styled, Toolbar } from '@mui/material'
import clsx from 'clsx'
import { ButtonGroup } from '../../../buttons/ButtonGroup'
import EditIcon from '@mui/icons-material/EditRounded'
import VisibilityIcon from '@mui/icons-material/VisibilityRounded'
import { AllState } from '../../../../../store'
import { useSelector } from 'react-redux'
import TemplateSelect from '../../../../containers/DescriptionTemplate/TemplateSelect'
import { Template } from '../../../../containers/DescriptionTemplate/TemplateList'
import { normalize } from '../../../../../utils/multilineText'
import { FontSize } from '../../../../../styles/commonStyles'
import './styles.scss'
import TextRotationIcon from '@mui/icons-material/TextRotationNoneRounded'
import { useEditorState } from './hooks/editor'
import { useMultilineEditor } from './hooks/useMultilineEditor'
import { EditorType, MarkdownType } from './types'
import { RichTextView } from './RichTextView'
import { MarkdownEditView } from './MarkdownEditView'
import { MarkdownPreviewView } from './MarkdownPreviewView'
import { MarkdownSideBySideView } from './MarkdownSideBySideView'

export { EditorType, MarkdownType } from './types'

const TextEditorOptions = [
  {
    labelId: 'multiline.switch.draft',
    value: EditorType.Draft,
  },
  {
    labelId: 'multiline.switch.markdown',
    value: EditorType.Markdown,
  },
]

const MarkdownTypeOptions = [
  {
    labelId: 'preview',
    value: MarkdownType.Preview,
    icon: <VisibilityIcon fontSize="small" />,
  },
  {
    labelId: 'sidebyside',
    value: MarkdownType.SideBySide,
    icon: <TextRotationIcon fontSize="small" />,
  },
  {
    labelId: 'edit',
    value: MarkdownType.Edit,
    icon: <EditIcon fontSize="small" />,
  },
]

type MultilineTextEditorProps = {
  dataUuid?: string
  dataLockVersion?: number
  externalId?: string
  value: string
  setValue?: Function
  disableSbsMode?: boolean
  fontSize?: FontSize
}

type AlertOption = {
  message: string
  submitHandler?: Function
}

const MultilineTextEditor = ({
  value,
  setValue,
  dataUuid,
  dataLockVersion,
  externalId,
  disableSbsMode,
}: MultilineTextEditorProps) => {
  const intl = useIntl()
  const projectUuid = useSelector((state: AllState) => state.project.selected)

  const {
    initialized,
    editorType,
    changeEditorType,
    markdownType,
    changeMarkdownType,
    templateUuid,
    changeTemplateUuid,
  } = useEditorState(
    dataUuid,
    !!normalize(value || '') ? MarkdownType.Preview : MarkdownType.Edit,
    disableSbsMode
  )

  const {
    previewText,
    loading,
    switchEditor,
    applyContent,
    hasContent,
    onDropAcceptedDraft,
    onDropAcceptedMarkdown,
    onPaste,
    onPasteCaptureIntoRichText,
    registerRichTextEditor,
    registerMarkdownSourceEditor,
    onContentChange,
  } = useMultilineEditor({
    editorType,
    initialized,
    value,
    dataLockVersion,
    setValue,
    formatMessage: intl.formatMessage,
  })

  const unregisterRichTextEditor = () => registerRichTextEditor(null)
  const unregisterMarkdownSourceEditor = () =>
    registerMarkdownSourceEditor(null)

  // スタイルボタン用のslot。各 View が createPortal でここに描画する。
  // 見た目上は親ツールバー内（モード切替と同じグレー領域）にStyleButtonsが収まる。
  const [toolbarSlot, setToolbarSlot] = useState<HTMLElement | null>(null)

  const [openTemplate, setOpenTemplate] = useState<boolean>(false)
  const [openAlert, setOpenAlert] = useState<boolean>(false)
  const [alertOption, setAlertOption] = useState<AlertOption>()

  const setTemplate = (template: Template) => {
    const onSubmit = () => {
      applyContent(template.text)
      changeTemplateUuid(template.uuid)
    }
    if (hasContent()) {
      setOpenAlert(true)
      setAlertOption({
        message: intl.formatMessage({
          id: 'template.paste.alert',
        }),
        submitHandler: () => {
          onSubmit()
          closeAlert()
        },
      })
      setOpenTemplate(false)
    } else {
      onSubmit()
    }
    setOpenTemplate(false)
  }

  const handleSwitchEditorType = (newEditorType: EditorType) => {
    switchEditor()
    changeEditorType(newEditorType)
    setOpenAlert(false)
  }

  const openSwitchAlert = (newEditorType: EditorType) => {
    setOpenAlert(true)
    setAlertOption({
      message: intl.formatMessage({
        id: 'multiline.switch.alert',
      }),
      submitHandler: () => handleSwitchEditorType(newEditorType),
    })
  }

  const closeAlert = () => {
    setOpenAlert(false)
    setAlertOption(undefined)
  }

  const markdownTypeOptions = MarkdownTypeOptions.filter(
    option => !(disableSbsMode && option.value === MarkdownType.SideBySide)
  ).map(option => ({
    ...option,
    label: intl.formatMessage({ id: option.labelId }),
  }))

  if (!initialized) return <></>

  const markdownEditView = (
    <MarkdownEditView
      loading={loading}
      toolbarSlot={toolbarSlot}
      onDropAccepted={onDropAcceptedMarkdown}
      onEditorReady={registerMarkdownSourceEditor}
      onEditorDestroy={unregisterMarkdownSourceEditor}
      onContentChange={onContentChange}
    />
  )

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <MultilineToolbar>
        <MultilineToolbarGroup sx={{ display: 'flex' }}>
          <ButtonGroup
            value={editorType}
            options={TextEditorOptions.map(option => ({
              ...option,
              label: intl.formatMessage({ id: option.labelId }),
            }))}
            onChange={openSwitchAlert}
          />
          {editorType === EditorType.Markdown && (
            <ButtonGroup
              value={markdownType}
              options={markdownTypeOptions}
              onChange={(value: MarkdownType) => changeMarkdownType(value)}
            />
          )}
          {externalId && (
            <>
              <TemplateSelect
                projectUuid={projectUuid}
                externalId={externalId}
                selectedUuid={templateUuid}
                onChange={(template: Template) => setTemplate(template)}
                openTemplate={openTemplate}
              />
              <IconButton
                size="small"
                onClick={() => setOpenTemplate(true)}
                sx={{ padding: '3px 5px' }}
              >
                <Tooltip message={intl.formatMessage({ id: 'template.edit' })}>
                  <WebIcon color="action" />
                </Tooltip>
              </IconButton>
              <TemplateDialog
                open={openTemplate}
                externalId={externalId}
                onClose={() => setOpenTemplate(false)}
                editor={editorType}
              />
            </>
          )}
        </MultilineToolbarGroup>
        <MultilineToolbarGroup ref={setToolbarSlot} />
      </MultilineToolbar>
      <MultilineEditorBox
        className={clsx({
          'markdown-body': editorType === EditorType.Markdown,
          'description-tab-markup-root': editorType === EditorType.Draft,
        })}
      >
        <Box sx={{ paddingLeft: '20px', width: '100%', height: '100%' }}>
          {editorType === EditorType.Draft && (
            <RichTextView
              loading={loading}
              toolbarSlot={toolbarSlot}
              onDropAccepted={onDropAcceptedDraft}
              onPaste={onPaste}
              onPasteCapture={onPasteCaptureIntoRichText}
              onEditorReady={registerRichTextEditor}
              onEditorDestroy={unregisterRichTextEditor}
              onContentChange={onContentChange}
            />
          )}
          {editorType === EditorType.Markdown &&
            markdownType === MarkdownType.Edit &&
            markdownEditView}
          {editorType === EditorType.Markdown &&
            markdownType === MarkdownType.Preview && (
              <MarkdownPreviewView
                previewText={previewText}
                markdownType={markdownType}
              />
            )}
          {editorType === EditorType.Markdown &&
            markdownType === MarkdownType.SideBySide && (
              <MarkdownSideBySideView
                previewText={previewText}
                markdownType={markdownType}
              >
                {markdownEditView}
              </MarkdownSideBySideView>
            )}
        </Box>
      </MultilineEditorBox>
      <AlertDialog
        isOpen={openAlert}
        message={alertOption?.message}
        submitButtonTitle={intl.formatMessage({ id: 'yes' })}
        submitHandler={alertOption?.submitHandler}
        closeButtonTitle={intl.formatMessage({ id: 'no' })}
        closeHandler={closeAlert}
      />
    </Box>
  )
}

const MultilineToolbar = styled(Toolbar)({
  '&.MuiToolbar-root': {
    padding: '5px 0 0 0',
    border: 0,
    borderRadius: '0 0 5px 5px',
    backgroundColor: '#f1f1f1',
    marginBottom: 0,
    minHeight: 41,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    '&:after': {
      borderColor: 'rgba(255, 255, 255, 0)',
      borderTopColor: '#333',
      borderWidth: '4px',
      marginLeft: '-4px',
    },
    '&:before': {
      borderColor: 'rgba(221, 221, 221, 0)',
      borderTopColor: '#111',
      borderWidth: '6px',
      marginLeft: '-6px',
    },
  },
})
const MultilineToolbarGroup = styled(Box)({ padding: '0 8px' })

const MultilineEditorBox = styled(Box)({
  height: 'calc(100% - 50px)',
  width: '100%',
  display: 'flex',
  flexFlow: 'column',
  overflowX: 'scroll',
  padding: '5px 0px',
  fontSize: FontSize.LARGE,
  flexGrow: 1,
})

// connect(pure: true) が担っていた props 浅比較の再レンダリング抑止を維持する
export default React.memo(MultilineTextEditor)
